/**
 * Geçmiş çalıştırmalar. Satıra tıklanınca kayıtlı analiz açılır — sonuç
 * kayıtta saklandığı için yeniden çalıştırmaya gerek yok. İstenirse kaydın
 * planı düzenleme tablosuna da yüklenebilir.
 */
import { useCallback, useEffect, useState } from "react";
import { Button, Drawer, Table, Tag, Tooltip, message } from "antd";
import { ArrowLeftOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import AnalizPaneli from "./AnalizPaneli";
import { gecmis, hataMesaji } from "./api";
import { sayiYaz } from "./bicim";

/** "2026-09-18T14:00:00" → "18.09.2026 14:00" (saat dilimine kaydırmadan). */
const tarihYaz = (t) => {
  const s = String(t ?? "");
  if (s.length < 16) return s || "–";
  return `${s.slice(8, 10)}.${s.slice(5, 7)}.${s.slice(0, 4)} ${s.slice(11, 16)}`;
};

export default function GecmisCekmecesi({ acik, onKapat, onPlaniYukle }) {
  const { t } = useTranslation();
  const h = (k, o) => t(`simulation.historyPanel.${k}`, o);

  const [kayitlar, setKayitlar] = useState([]);
  const [yukleniyor, setYukleniyor] = useState(false);
  const [secili, setSecili] = useState(null);

  const getir = useCallback(async () => {
    setYukleniyor(true);
    try {
      setKayitlar(await gecmis(50));
    } catch (err) {
      message.error(hataMesaji(err, t("simulation.err.history")));
    } finally {
      setYukleniyor(false);
    }
  }, [t]);

  // Her açılışta tazelenir; kapanınca detaydan listeye dönülür.
  useEffect(() => {
    if (acik) getir();
    else setSecili(null);
  }, [acik, getir]);

  return (
    <Drawer
      open={acik}
      onClose={onKapat}
      width={Math.min(760, window.innerWidth - 24)}
      title={
        secili ? (
          <div className="flex items-center gap-2">
            <Button
              size="small"
              type="text"
              icon={<ArrowLeftOutlined />}
              onClick={() => setSecili(null)}
            />
            {h("runTitle", { id: secili.id })}
          </div>
        ) : (
          h("title")
        )
      }
      extra={
        secili ? (
          // Plan gönderilmeden yapılan koşularda model planı DB'den okuyor ve
          // kayda plan yazılmıyor; boş planı yüklemek tabloyu silerdi.
          <Tooltip title={secili.plan?.length ? null : h("noPlanTip")}>
            <Button
              size="small"
              disabled={!secili.plan?.length}
              onClick={() => {
                onPlaniYukle(secili);
                onKapat();
              }}
            >
              {h("loadPlan")}
            </Button>
          </Tooltip>
        ) : (
          <Button size="small" onClick={getir} loading={yukleniyor}>
            {h("refresh")}
          </Button>
        )
      }
    >
      {secili ? (
        <AnalizPaneli
          sonuc={secili.result}
          baslik={`${tarihYaz(secili.created_at)} · ${secili.created_by ?? "–"}`}
        />
      ) : (
        <Table
          className="sim-tablo"
          size="small"
          rowKey="id"
          loading={yukleniyor}
          dataSource={kayitlar}
          pagination={{ pageSize: 15, size: "small", hideOnSinglePage: true }}
          onRow={(r) => ({ onClick: () => setSecili(r), style: { cursor: "pointer" } })}
          locale={{ emptyText: h("empty") }}
          columns={[
            { title: "#", dataIndex: "id", width: 56 },
            { title: h("cols.date"), dataIndex: "created_at", width: 140, render: tarihYaz },
            { title: h("cols.user"), dataIndex: "created_by", render: (v) => v ?? "–" },
            { title: h("cols.sourcePlan"), dataIndex: "source_order_batch_id", width: 100 },
            { title: h("cols.draft"), dataIndex: "draft_id", width: 72, render: (v) => v ?? "–" },
            {
              title: h("cols.shortage"),
              key: "acik",
              width: 72,
              align: "right",
              render: (_, r) => sayiYaz(r.result?.ozet?.toplam_acik),
            },
            {
              title: h("cols.status"),
              dataIndex: "is_published",
              width: 110,
              render: (v) =>
                v ? <Tag color="green">{h("published")}</Tag> : <Tag>{h("trial")}</Tag>,
            },
          ]}
        />
      )}
    </Drawer>
  );
}
